import { FC, useEffect, useState } from "react";
import { RecurrentProduct } from "../../../../../../type/entities/recurrent-product-type";
import * as yup from "yup";
import { RecurrentProductService } from "../../../../../../services/recurrentProducts/recurrentProducts";
import { useAtom } from "jotai";
import { formsSubmitAtom } from "../../../../../../atom/atom";
import useHandleErrors from "../../../../../../hooks/useHandleErrors";
import useCompanies from "../../../../../../hooks/api-calls/useCompanies";
import useProducts from "../../../../../../hooks/api-calls/useProducts";
import { useFormik } from "formik";
import { usePrivilege } from "../../../../../../components/priviledge/PriviledgeProvider";
import { toast } from "sonner";
import RequiredInput from "../../../../../../components/forms/InputHOC";
import CustomSelect from "../../../../../../components/forms/CustomSelect";
import { Input, Textarea } from "rizzui";

interface RecurrentProductFormProps {
    setOpenModal: any;
    refetch: () => void;
    product?: RecurrentProduct | null;
    formRef?: any;
    headquarterId?: string | null;
}


const schema = yup.object({
    headquarterId: yup.string().required('Campo requerido'),
    productId: yup.string().required('Campo requerido'),
    comments: yup.string().nullable(),
})

const superAdminSchema = schema.concat(yup.object({
    company: yup.string().required('La compañía es obligatoria'),
}))


const RecurrentProductsForm: FC<RecurrentProductFormProps> = ({
    setOpenModal,
    refetch,
    product,
    formRef,
    headquarterId,
}) => {
    
    // STATES 

    const service = new RecurrentProductService();
    const isEdit = product !== null;
    const [isSearched, setIsSearched] = useState(false);

    // HOOKS

    const [, setIsSubmitting] = useAtom(formsSubmitAtom);
    const { handleErrors } = useHandleErrors();
    const { getCompaniesList } = useCompanies();
    const { getProductsList } = useProducts();
    const { userCan } = usePrivilege();

    // FORMIK

    const formik = useFormik({
        initialValues: {
            id: product?.id ?? '',
            headquarterId: headquarterId ?? '',
            productId: product?.product?.id ?? '',
            estimatedQuantity: product?.estimatedQuantity ?? 0,
            comments: product?.comments ?? '',
            company: product?.company.id ?? '',
        },
        validationSchema: userCan('list', 'company') ? superAdminSchema : schema,
        validateOnBlur: false,

        onSubmit: async (values) => {
            if (!isEdit) {
                handleCreate(values);
            } else {
                handleEdit(values);
            }
        },
    });


    // FUNCTIONS

    //-----------------------------------------------------------------------------------------------------------------
    /**
     *  @EN HANDLES RECURRENT PRODUCT CREATION
     *  @ES MANEJA LA CREACION DE UN PRODUCTO RECURRENTE
     */
    //-----------------------------------------------------------------------------------------------------------------
    const handleCreate = async (values: any) => {
        try {
            setIsSubmitting(true);
            const response = await (await service.createRecurrentProduct(values)).getResponseData();
            if (response.success) {
                refetch();
                setOpenModal(false);
                toast.success('Producto recurrente creado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        } finally {
            setIsSubmitting(false);
        }
    }
    //-----------------------------------------------------------------------------------------------------------------

    //-----------------------------------------------------------------------------------------------------------------
    /**
     *  @EN HANDLES RECURRENT PRODUCT EDITION
     *  @ES MANEJA LA EDICION DE UN PRODUCTO RECURRENTE
     */
    //-----------------------------------------------------------------------------------------------------------------
    const handleEdit = async (values: any) => {
        try {
            setIsSubmitting(true);
            const response = await (await service.editRecurrentProduct(values)).getResponseData();
            if (response.success) {
                refetch();
                setOpenModal(false);
                toast.success('Producto recurrente editado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        } finally {
            setIsSubmitting(false);
        }
    }
    //-----------------------------------------------------------------------------------------------------------------

    // USE EFFECTS

    useEffect(() => {
        setIsSubmitting(formik.isSubmitting);
    }, [formik.isSubmitting, setIsSubmitting]);

    useEffect(() => {
        if (!formik.values.company && getCompaniesList().length > 0 && !isSearched) {
            let defaultCompany = getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU');
            formik.setFieldValue('company', defaultCompany?.value ?? '');
            setIsSearched(true);
        }
    }, [getCompaniesList]);

    // RENDER

    return (
        <div className="@container">
            <form onSubmit={formik.handleSubmit} ref={formRef ? formRef : undefined}>
                <div className={'md:col-span-12'}>
                    <div className='md:grid md:grid-cols-12 gap-4'>
                        
                        {userCan('list', 'company') && (
                            <CustomSelect
                            required
                            isSearchable
                            id={'company'}
                            label="Compañia"
                            containerClassName='md:col-span-12'
                            value={
                                formik.values.company ?
                                { value: formik.values.company, label: getCompaniesList()?.find((g: any) => g.value === formik.values.company)?.label } :
                                getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU')
                            }
                            options={getCompaniesList()}
                            onChange={
                                (e: any) => { formik.setFieldValue('company', e?.value) }
                            }
                            error={formik.errors.company}
                            />
                        )}

                        <CustomSelect
                            required
                            isSearchable
                            id={'productId'}
                            label="Producto"
                            containerClassName='md:col-span-12'
                            value={{ value: formik.values.productId, label: getProductsList()?.find((g: any) => g.value === formik.values.productId)?.label }}
                            options={getProductsList()}
                            onChange={
                                (e: any) => { formik.setFieldValue('productId', e?.value) }
                            }
                            error={formik.errors.productId}
                        />

                        <Input
                            type="number"
                            id={'estimatedQuantity'}
                            label="Cantidad estimada"
                            className='md:col-span-12'
                            value={formik.values.estimatedQuantity}
                            onChange={(e: any) => formik.setFieldValue('estimatedQuantity', e.target.value)}
                            error={formik.errors.estimatedQuantity}
                        />

                        <Textarea
                            id={'comments'}
                            label="Comentarios"
                            className='md:col-span-12'
                            value={formik.values.comments}
                            onChange={(e: any) => formik.setFieldValue('comments', e.target.value)}
                            error={formik.errors.comments}
                        />
                    </div>
                </div>
            </form>
        </div>
    )
}

export default RecurrentProductsForm;